import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";
import type { components } from "../api/schema";

export type LicenceStatus = components["schemas"]["LicenceStatus"];

const KEY = ["licence"] as const;

export function useLicence(enabled = true) {
  return useQuery({
    queryKey: KEY,
    queryFn: () => unwrap(api.GET("/api/licence")),
    enabled,
    // The evaluation ends on its own.
    refetchInterval: 5 * 60_000,
  });
}

/** Hours left, rounded up, for the evaluation banner. */
export function hoursLeft(s: LicenceStatus, now = Date.now()): number | null {
  if (s.state !== "evaluation" || !s.evaluation_ends) return null;
  return Math.max(
    0,
    Math.ceil((Date.parse(s.evaluation_ends) - now) / 3_600_000),
  );
}

function summary(s: LicenceStatus): string {
  const ends = s.evaluation_ends
    ? new Date(s.evaluation_ends).toLocaleString()
    : "";
  switch (s.state) {
    case "licensed":
      return "AVGV is licensed.";
    case "evaluation":
      return `This is an evaluation, which ends ${ends}. Install a licence to keep using AVGV.`;
    case "evaluation_over":
      return `The evaluation ended ${ends}. Install a licence to use AVGV again: your recordings are kept.`;
    case "not_covered":
      return `The licence's updates ended on ${s.licence?.updates_until}, before this version was released (${s.build_date}). Renew the licence, or install a version released before then: your recordings are kept.`;
    default:
      return "This build has no licensing.";
  }
}

async function downloadRequest() {
  const { data, error, response } = await api.GET("/api/licence/request", {
    parseAs: "blob",
  });
  if (!data)
    throw new Error(
      (error as { message?: string } | undefined)?.message ??
        `HTTP ${response.status}`,
    );
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "avgv-licence-request.json";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function LicencePage({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const licence = useLicence();
  const [message, setMessage] = useState<string | null>(null);
  const install = useMutation({
    mutationFn: (text: string) =>
      unwrap(api.PUT("/api/licence", { body: { text } })),
    onSuccess: (s) => {
      qc.setQueryData(KEY, s);
      setMessage(null);
      // Everything else was refused while locked.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== KEY[0] });
    },
  });
  const request = useMutation({ mutationFn: downloadRequest });
  const s = licence.data;
  if (licence.isError)
    return <Alert severity="error">{licence.error.message}</Alert>;
  if (!s) return null;
  const l = s.licence;
  return (
    <Stack spacing={2} sx={{ maxWidth: 760 }}>
      <Typography variant="h5" component="h2">
        Licence
      </Typography>
      <Alert
        severity={
          s.locked ? "error" : s.state === "evaluation" ? "warning" : "success"
        }
      >
        {summary(s)}
      </Alert>
      {s.problem && (
        <Alert severity="warning">
          The installed licence isn't in use: {s.problem}
        </Alert>
      )}
      {l && (
        <Paper variant="outlined">
          <Table size="small" aria-label="Licence terms">
            <TableBody>
              <TableRow>
                <TableCell component="th">Licensed to</TableCell>
                <TableCell>{l.customer}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th">Licence</TableCell>
                <TableCell>{l.id}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th">Edition</TableCell>
                <TableCell>{l.edition}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th">Issued</TableCell>
                <TableCell>{l.issued}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th">Updates until</TableCell>
                <TableCell>{l.updates_until}</TableCell>
              </TableRow>
              {l.max_users != null && (
                <TableRow>
                  <TableCell component="th">Accounts</TableCell>
                  <TableCell>up to {l.max_users} in use</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
      {s.state !== "open" && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6" component="h3">
              Getting a licence
            </Typography>
            <Typography variant="body2">
              A licence is for one{" "}
              {s.edition === "server" ? "server installation" : "computer"},
              named by its machine code. Send the licence request file (or the
              code) to Atlantic Technology, then install the licence file you
              are sent. Neither needs an internet connection here.
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Machine code
              </Typography>
              <Typography
                sx={{ fontFamily: "monospace", fontSize: "1.2rem" }}
                data-testid="machine-code"
              >
                {s.machine_code}
              </Typography>
            </Box>
            {canManage ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap" }}
                useFlexGap
              >
                <Button
                  variant="outlined"
                  onClick={() => request.mutate()}
                  disabled={request.isPending}
                >
                  Download licence request
                </Button>
                <Button variant="contained" component="label">
                  Install licence file…
                  <input
                    hidden
                    type="file"
                    accept=".avgvlic,.json,application/json"
                    aria-label="Licence file"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f)
                        f.text().then(
                          (t) => install.mutate(t),
                          (err: unknown) => setMessage(String(err)),
                        );
                    }}
                  />
                </Button>
              </Stack>
            ) : (
              <Typography variant="body2">
                Ask an administrator to install a licence.
              </Typography>
            )}
            {request.isError && (
              <Alert severity="error">{request.error.message}</Alert>
            )}
            {install.isError && (
              <Alert severity="error">{install.error.message}</Alert>
            )}
            {message && <Alert severity="error">{message}</Alert>}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
